"use client";

import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import Image from "next/image";
import Link from "next/link";
import { BookOpen, LogOut, Settings, Sparkles, User, UserCircle } from "lucide-react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useAuth } from "@/components/providers/auth-provider";
import type { AuthUser } from "@/features/auth/api";
import { cfImageUrl } from "@/lib/cloudflare-image";
import { cn } from "@/lib/utils";
import { signInHref } from "@/lib/post-login-redirect";
import { useIdePlan } from "../hooks";
import { toPublicProfileSlug, userInitials } from "../lib/public-profile-slug";
const overflowMenuItemClass =
  "flex min-h-[44px] w-full cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-white/90 outline-none hover:bg-white/10";

const ghostSignIn =
  "inline-flex shrink-0 items-center justify-center rounded-lg border border-white/20 bg-transparent px-3 text-sm font-medium text-white/90 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C]";

const ghostSignInLg = cn(ghostSignIn, "h-11 min-h-[44px] md:h-10 md:min-h-0");

const upgradeBtnGradient =
  "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-[#E94D1D] via-[#f05a28] to-[#FFAB8F] px-3 text-sm font-semibold text-white shadow-md transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/55 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C]";

const upgradeBtnLg = cn(upgradeBtnGradient, "h-11 min-h-[44px] md:h-10 md:min-h-0");

const iconBtn =
  "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/5 text-white/90 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C] md:h-10 md:w-10";

/**
 * `LN-NAV01`: programmers gather in the Playground, so it carries the same
 * "Learn" cross-link as the Judge topbar. A contest-temp account never sees
 * it: this account area returns nothing at all for that account kind, above
 * and below, and `ContestTempNav` is what renders instead.
 */
function PlaygroundLearnLink({ locale }: { locale: string }) {
  const t = useTranslations("nav");
  return (
    <Link
      href={`/${locale}/learn`}
      className={cn(ghostSignIn, iconBtn)}
      aria-label={t("learn")}
      title={t("learn")}
    >
      <BookOpen className="h-4 w-4" aria-hidden />
    </Link>
  );
}

function AvatarTrigger({
  user,
  className,
}: {
  user: AuthUser;
  className?: string;
}) {
  const initials = userInitials(user);
  const url = user.avatarImageId ? cfImageUrl(user.avatarImageId, "avatar") : "";

  return (
    <span
      className={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-white/20 to-white/5 text-xs font-semibold text-white ring-2 ring-white/10",
        className,
      )}
    >
      {url ? (
        <Image
          src={url}
          alt={user.name}
          width={40}
          height={40}
          className="h-full w-full object-cover"
        />
      ) : (
        <span aria-hidden>{initials}</span>
      )}
    </span>
  );
}

function AccountDropdown({
  user,
  locale,
  profileHref,
  tierLabel,
  children,
  align = "end",
}: {
  user: AuthUser;
  locale: string;
  profileHref: string;
  /** The tier, said here below `xl` where the toolbar row has no space for a badge. */
  tierLabel?: string | undefined;
  children: React.ReactNode;
  align?: "start" | "end";
}) {
  const t = useTranslations("playground");
  const router = useRouter();
  const { logout } = useAuth();

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{children}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-[220] min-w-[240px] rounded-xl border border-white/10 bg-[#141416] p-1 shadow-2xl"
          sideOffset={6}
          align={align}
          collisionPadding={8}
        >
          <div className="px-2.5 py-2">
            <p className="truncate text-sm font-medium text-white">{user.name}</p>
            <p className="truncate text-xs text-white/50">{user.email}</p>
            {tierLabel ? (
              <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-wide text-white/70">
                {tierLabel}
              </p>
            ) : null}
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-white/10" />
          <DropdownMenu.Item asChild>
            <Link
              href={`/${locale}/dashboard/playground`}
              className="flex cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-white/90 outline-none data-[highlighted]:bg-white/10"
            >
              <UserCircle className="h-4 w-4 shrink-0 text-[#E94D1D]" aria-hidden />
              {t("accountMenuMySnippets")}
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <Link
              href={profileHref}
              className="flex cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-white/90 outline-none data-[highlighted]:bg-white/10"
            >
              <User className="h-4 w-4 shrink-0 text-white/70" aria-hidden />
              {t("accountMenuMyProfile")}
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <Link
              href={`/${locale}/dashboard/settings`}
              className="flex cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-white/90 outline-none data-[highlighted]:bg-white/10"
            >
              <Settings className="h-4 w-4 shrink-0 text-white/70" aria-hidden />
              {t("accountMenuSettings")}
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-white/10" />
          <DropdownMenu.Item
            className="flex cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-white/90 outline-none data-[highlighted]:bg-white/10"
            onSelect={(e) => {
              e.preventDefault();
              void logout().then(() => router.refresh());
            }}
          >
            <LogOut className="h-4 w-4 shrink-0 text-white/70" aria-hidden />
            {t("accountMenuSignOut")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export type PlaygroundToolbarAccountProps = {
  locale: string;
  /** The page to come back to after signing in, a path (`signInHref` encodes it). */
  loginNext: string;
  onUpgradeClick: () => void;
};

export function PlaygroundToolbarAccount({
  locale,
  loginNext,
  onUpgradeClick,
}: PlaygroundToolbarAccountProps) {
  const t = useTranslations("playground");
  const { user, isAuthenticated, isLoading } = useAuth();

  const planQuery = useIdePlan(isAuthenticated && !isLoading);
  const plan = planQuery.data?.plan;
  const isIdeUnlocked = plan === "pro" || plan === "internal";
  const planReady = !planQuery.isLoading && !planQuery.isError;
  const showInternalBadge = Boolean(user?.isInternal);

  const loginHref = signInHref(locale, loginNext);
  const profileSlug = user ? toPublicProfileSlug(user) : "me";
  const profileHref = `/${locale}/u/${encodeURIComponent(profileSlug)}`;

  if (isLoading) {
    return (
      <div className="hidden shrink-0 md:flex md:h-10 md:w-[120px] md:items-center md:justify-end lg:w-[140px]" aria-hidden>
        <div className="h-8 w-8 animate-pulse rounded-full bg-white/10" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="hidden shrink-0 md:flex md:items-center md:gap-2">
        <PlaygroundLearnLink locale={locale} />
        <Link
          href={loginHref}
          className={cn(ghostSignIn, iconBtn, "md:flex lg:hidden")}
          aria-label={t("toolbarSignIn")}
          title={t("toolbarSignIn")}
        >
          <User className="h-4 w-4" aria-hidden />
        </Link>
        <Link href={loginHref} className={cn(ghostSignInLg, "hidden lg:inline-flex")}>
          {t("toolbarSignIn")}
        </Link>
      </div>
    );
  }

  if (!user) return null;
  if (user.accountKind === "contest_temp") return null;

  return (
    <>
      {/*
        Tablet and small desktop (md to xl): the upgrade action and the avatar,
        with no tier badge. A truncated "Internal a…" pill was costing the
        toolbar row about 110px it does not have at 768px, where it pushed the
        left cluster onto the Run button (measured 2026-09-04). The tier is not
        an action, so below `xl` it is said inside the account menu instead.
      */}
      <div className="hidden shrink-0 items-center gap-1.5 md:flex xl:hidden">
        <PlaygroundLearnLink locale={locale} />
        {planReady && !showInternalBadge && !isIdeUnlocked ? (
          <button
            type="button"
            onClick={onUpgradeClick}
            className={iconBtn}
            aria-label={t("toolbarUpgradePro")}
            title={t("toolbarUpgradePro")}
          >
            <Sparkles className="h-4 w-4 text-[#FFAB8F]" aria-hidden />
          </button>
        ) : null}

        <AccountDropdown
          user={user}
          locale={locale}
          profileHref={profileHref}
          align="end"
          tierLabel={
            planReady && showInternalBadge
              ? t("toolbarInternalBadge")
              : planReady && isIdeUnlocked
                ? t("toolbarProBadge")
                : undefined
          }
        >
          <button
            type="button"
            className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C]"
            aria-label={t("accountMenuOpen")}
          >
            <AvatarTrigger user={user} className="h-9 w-9" />
          </button>
        </AccountDropdown>
      </div>

      {/* Wide desktop xl+: the row has room for the tier badge itself. */}
      <div className="hidden shrink-0 items-center gap-2 xl:flex">
        <PlaygroundLearnLink locale={locale} />
        {planReady && showInternalBadge ? (
          <span className="rounded-md border border-sky-500/30 bg-sky-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-sky-200/95">
            {t("toolbarInternalBadge")}
          </span>
        ) : planReady && isIdeUnlocked ? (
          <span className="rounded-md border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-300/95">
            {t("toolbarProBadge")}
          </span>
        ) : planReady ? (
          <button type="button" onClick={onUpgradeClick} className={upgradeBtnLg}>
            {t("toolbarUpgradePro")}
          </button>
        ) : null}

        <AccountDropdown user={user} locale={locale} profileHref={profileHref} align="end">
          <button
            type="button"
            className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C]"
            aria-label={t("accountMenuOpen")}
          >
            <AvatarTrigger user={user} className="h-9 w-9" />
          </button>
        </AccountDropdown>
      </div>

    </>
  );
}

/** Mobile overflow footer: upgrade + account links (no nested avatar dropdown). */
export function PlaygroundToolbarAccountMobileFooter({
  locale,
  loginNext,
  onCloseOverflow,
  onUpgradeClick,
}: {
  locale: string;
  loginNext: string;
  onCloseOverflow?: () => void;
  onUpgradeClick: () => void;
}) {
  const t = useTranslations("playground");
  const tNav = useTranslations("nav");
  const router = useRouter();
  const { user, isAuthenticated, isLoading, logout } = useAuth();

  const planQuery = useIdePlan(isAuthenticated && !isLoading);
  const plan = planQuery.data?.plan;
  const isIdeUnlocked = plan === "pro" || plan === "internal";
  const planReady = !planQuery.isLoading && !planQuery.isError;
  const showInternalBadge = Boolean(user?.isInternal);

  const loginHref = signInHref(locale, loginNext);
  const profileSlug = user ? toPublicProfileSlug(user) : "me";
  const profileHref = `/${locale}/u/${encodeURIComponent(profileSlug)}`;

  const close = () => onCloseOverflow?.();

  if (isLoading) {
    return (
      <div className="border-t border-white/10 pt-2">
        <div className="h-10 w-full animate-pulse rounded-lg bg-white/5" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="border-t border-white/10 pt-2">
        <Link
          href={`/${locale}/learn`}
          className={overflowMenuItemClass}
          onClick={close}
        >
          <BookOpen className="h-4 w-4 shrink-0 text-[#E94D1D]" aria-hidden />
          {tNav("learn")}
        </Link>
        <Link
          href={loginHref}
          className={cn(ghostSignIn, "flex h-11 w-full items-center justify-center")}
          onClick={close}
        >
          {t("toolbarSignIn")}
        </Link>
      </div>
    );
  }

  if (!user) return null;
  if (user.accountKind === "contest_temp") return null;

  return (
    <div className="border-t border-white/10 pt-2">
      {planReady && showInternalBadge ? (
        <div className="mb-2 flex justify-center">
          <span className="rounded-md border border-sky-500/30 bg-sky-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-sky-200/95">
            {t("toolbarInternalBadge")}
          </span>
        </div>
      ) : planReady && isIdeUnlocked ? (
        <div className="mb-2 flex justify-center">
          <span className="rounded-md border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-300/95">
            {t("toolbarProBadge")}
          </span>
        </div>
      ) : planReady ? (
        <button
          type="button"
          onClick={() => {
            close();
            onUpgradeClick();
          }}
          className={cn(upgradeBtnGradient, "mb-2 flex h-11 w-full items-center justify-center gap-2")}
        >
          <Sparkles className="h-4 w-4" aria-hidden />
          {t("toolbarUpgradePro")}
        </button>
      ) : null}

        <div className="mb-2 flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2 py-2">
          <AvatarTrigger user={user} className="h-10 w-10 shrink-0 text-sm" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{user.name}</p>
            <p className="truncate text-xs text-white/50">{user.email}</p>
          </div>
        </div>

        <Link
          href={`/${locale}/learn`}
          className={overflowMenuItemClass}
          onClick={close}
        >
          <BookOpen className="h-4 w-4 shrink-0 text-[#E94D1D]" aria-hidden />
          {tNav("learn")}
        </Link>
        <Link
          href={`/${locale}/dashboard/playground`}
          className={overflowMenuItemClass}
          onClick={close}
        >
          <UserCircle className="h-4 w-4 shrink-0 text-[#E94D1D]" aria-hidden />
          {t("accountMenuMySnippets")}
        </Link>
        <Link href={profileHref} className={overflowMenuItemClass} onClick={close}>
          <User className="h-4 w-4 shrink-0 text-white/70" aria-hidden />
          {t("accountMenuMyProfile")}
        </Link>
        <Link
          href={`/${locale}/dashboard/settings`}
          className={overflowMenuItemClass}
          onClick={close}
        >
          <Settings className="h-4 w-4 shrink-0 text-white/70" aria-hidden />
          {t("accountMenuSettings")}
        </Link>
        <button
          type="button"
          className={overflowMenuItemClass}
          onClick={() => {
            close();
            void logout().then(() => router.refresh());
          }}
        >
          <LogOut className="h-4 w-4 shrink-0 text-white/70" aria-hidden />
          {t("accountMenuSignOut")}
        </button>
    </div>
  );
}
